<script setup lang="ts">
import { computed } from 'vue'

export type TextType =
  // Display / hero
  | 'display-lg' //   30px / 700
  | 'display' //      24px / 700
  | 'title-lg' //     20px / 600
  // Heading tier
  | 'page-title' //   18px / 600
  | 'heading' //      16px / 700
  | 'subheading' //   14.4px / 600 — outlier size, kept
  // Body tier
  | 'body' //         14px / 400  (line-height 1.6)
  | 'body-sm' //      13px / 400  (line-height 1.5)
  // Caption tier
  | 'caption' //      12px / 400  (muted)
  | 'caption-sm' //   10px / 500  (muted)

export type TextColor =
  | 'default'
  | 'muted'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'inverse'

export type TextAlign = 'left' | 'center' | 'right'
export type TextTransform = 'none' | 'uppercase' | 'lowercase' | 'capitalize'
export type TextWeight = 'regular' | 'medium' | 'semibold' | 'bold'
export type TextFamily = 'mono'

interface Props {
  /** Semantic HTML tag override (e.g. `is="li"`) */
  is?: string
  /** Size preset — 11 distinct sizes. Use `weight` for weight variations. */
  type?: TextType
  /**
   * Semantic color shortcut OR raw CSS color escape hatch.
   * Shortcuts: `default | muted | primary | success | warning | danger | info | inverse`.
   * Anything else (`var(--p-success-600)`, `#fff`, `rgb(...)`) applies as inline style.
   */
  color?: TextColor | (string & {})
  /** `text-align` */
  align?: TextAlign
  /** `text-transform` */
  transform?: TextTransform
  /** Single-line ellipsis when `true`. Line-clamp when `2 | 3 | 4 | 5 | 6`. */
  truncate?: boolean | number
  /** Orthogonal weight override on top of `type`'s default weight. */
  weight?: TextWeight
  /** Font family override — `mono` swaps to a monospace stack for IDs, IBANs, code. */
  family?: TextFamily
}

const props = defineProps<Props>()

const semanticColors = new Set<TextColor>([
  'default',
  'muted',
  'primary',
  'success',
  'warning',
  'danger',
  'info',
  'inverse',
])

const defaultTagByType: Record<TextType, string> = {
  'display-lg': 'h1',
  display: 'h1',
  'title-lg': 'h2',
  'page-title': 'h2',
  heading: 'h3',
  subheading: 'h4',
  body: 'p',
  'body-sm': 'p',
  caption: 'span',
  'caption-sm': 'span',
}

const tag = computed(() => props.is ?? (props.type ? defaultTagByType[props.type] : 'p'))

const isSemanticColor = (value?: string): value is TextColor =>
  !!value && semanticColors.has(value as TextColor)

const MIN_CLAMP_LINES = 2
const MAX_CLAMP_LINES = 6

const truncateClass = computed(() => {
  if (props.truncate === undefined || props.truncate === false) return null
  if (props.truncate === true || props.truncate === 1) return 'text--truncate'
  const clampLines = Math.floor(props.truncate)
  if (clampLines >= MIN_CLAMP_LINES && clampLines <= MAX_CLAMP_LINES) {
    return `text--clamp-${clampLines}`
  }
  return null
})

const classes = computed(() => [
  props.type ? `text--${props.type}` : null,
  props.align ? `text--align-${props.align}` : null,
  props.transform ? `text--transform-${props.transform}` : null,
  props.weight ? `text--weight-${props.weight}` : null,
  props.family ? `text--family-${props.family}` : null,
  isSemanticColor(props.color) ? `text--color-${props.color}` : null,
  truncateClass.value,
])

const inlineStyle = computed(() =>
  props.color && !isSemanticColor(props.color) ? { color: props.color } : undefined,
)
</script>

<template>
  <component :is="tag" :class="classes" :style="inlineStyle">
    <slot />
  </component>
</template>

<style scoped>
/* Reset browser margin on every typed text node */
.text--display-lg,
.text--display,
.text--title-lg,
.text--page-title,
.text--heading,
.text--subheading,
.text--body,
.text--body-sm,
.text--caption,
.text--caption-sm {
  margin: 0;
  letter-spacing: 0.4px;
}

/* ── Display tier ───────────────────────────────────────────── */
.text--display-lg {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.4px;
  color: var(--p-text-color);
}

.text--display {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.2px;
  color: var(--p-text-color);
}

.text--title-lg {
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--p-text-color);
}

/* ── Heading tier ───────────────────────────────────────────── */
.text--page-title {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--p-text-color);
}

.text--heading {
  font-size: 16px;
  font-weight: 700;
  color: var(--p-text-color);
}

.text--subheading {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--p-text-color);
}

/* ── Body tier ──────────────────────────────────────────────── */
.text--body {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.6;
  color: var(--p-text-color);
}

.text--body-sm {
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--p-text-color);
}

/* ── Caption tier ───────────────────────────────────────────── */
.text--caption {
  font-size: 12px;
  font-weight: 400;
  color: var(--p-text-muted-color);
}

.text--caption-sm {
  font-size: 10px;
  font-weight: 500;
  color: var(--p-text-muted-color);
}

/* ── Color shortcuts ────────────────────────────────────────── */
.text--color-default {
  color: var(--p-text-color);
}
.text--color-muted {
  color: var(--p-text-muted-color);
}
.text--color-primary {
  color: var(--p-primary-600);
}
.text--color-success {
  color: var(--p-success-600);
}
.text--color-warning {
  color: var(--p-warning-600);
}
.text--color-danger {
  color: var(--p-danger-600);
}
.text--color-info {
  color: var(--p-info-600);
}
.text--color-inverse {
  color: var(--p-primary-contrast-color);
}

/* ── Alignment ──────────────────────────────────────────────── */
.text--align-left {
  text-align: left;
}
.text--align-center {
  text-align: center;
}
.text--align-right {
  text-align: right;
}

/* ── Transform ──────────────────────────────────────────────── */
.text--transform-none {
  text-transform: none;
}
.text--transform-uppercase {
  text-transform: uppercase;
}
.text--transform-lowercase {
  text-transform: lowercase;
}
.text--transform-capitalize {
  text-transform: capitalize;
}

/* ── Font family override ────────────────────────────────────── */
.text--family-mono {
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
}

/* ── Weight override (orthogonal to `type`'s baked weight) ───── */
.text--weight-regular {
  font-weight: 400;
}
.text--weight-medium {
  font-weight: 500;
}
.text--weight-semibold {
  font-weight: 600;
}
.text--weight-bold {
  font-weight: 700;
}

/* ── Truncate / line clamp ─────────────────────────────────── */
.text--truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
}
.text--clamp-2,
.text--clamp-3,
.text--clamp-4,
.text--clamp-5,
.text--clamp-6 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.text--clamp-2 {
  -webkit-line-clamp: 2;
  line-clamp: 2;
}
.text--clamp-3 {
  -webkit-line-clamp: 3;
  line-clamp: 3;
}
.text--clamp-4 {
  -webkit-line-clamp: 4;
  line-clamp: 4;
}
.text--clamp-5 {
  -webkit-line-clamp: 5;
  line-clamp: 5;
}
.text--clamp-6 {
  -webkit-line-clamp: 6;
  line-clamp: 6;
}
</style>
